/**
 * Use this to parse SAP codes of products.
 * @param {string[]} products Array of SAP codes
 * @param {Map<string, string>} locs JS Map of SAP codes to names
 * @returns {string[]} Array of **unique** parsed SAP codes.
 */
export const getProductNames = (products = [], locs) =>
  Array.from(new Set(products)).map((key) => locs?.get(key)?.label ?? key);

export const getProductsList = (hostApps = [], optionalProducts = [], productsLocs, currentProduct = 'All') => {
  const products = [...hostApps, ...optionalProducts];
  const productNames = getProductNames(products, productsLocs);
  let index = null;
  let firstProduct = null;
  if (currentProduct && currentProduct !== 'All') {
    index = products?.indexOf(currentProduct);
    firstProduct = productNames[index];
  } else if (hostApps.length > 0) {
    const hostAppProductNames = getProductNames(hostApps, productsLocs);
    hostAppProductNames.sort(azSort);
    firstProduct = hostAppProductNames[0];
    index = productNames.indexOf(firstProduct);
  }
  if (index >= 0 && firstProduct) {
    productNames.splice(index, 1);
    productNames.sort(azSort);
    productNames.unshift(firstProduct);
  } else {
    productNames.sort(azSort);
  }
  return productNames;
};

export const azSort = (a, b) => {
  const nameA = a?.toUpperCase();
  const nameB = b?.toUpperCase();
  if (nameA < nameB) {
    return -1;
  }
  if (nameA > nameB) {
    return 1;
  }
  return 0;
};

export const makeVersionString = () =>
  [`${process.env.npm_package_version}`, process.env.NODE_ENV === 'production' ? '' : `-${process.env.NODE_ENV}`].join(
    ''
  );

export const getCorrectUrlSlug = (urlSelector = '') =>
  urlSelector
    ?.toLowerCase()
    .replace(/[^a-z0-9\s-]+/g, '')
    .trim()
    .replace(/\s/g, '-')
    .replace(/-+/g, '-')
    .trim()
    .replace(/-/g, ' ')
    .trim()
    .replace(/\s/g, '-');

export const SAP_CODE_TO_ICON_ID = Object.freeze({
  APRO: 'svg-icon-acrobat-pro',
  AEFT: 'svg-icon-after-effects',
  FLPR: 'svg-icon-animate',
  AUDT: 'svg-icon-audition',
  KBRG: 'svg-icon-bridge',
  ESHR: 'svg-icon-dimension',
  DRWV: 'svg-icon-dreamweaver',
  ILST: 'svg-icon-illustrator',
  AICY: 'svg-icon-incopy',
  IDSN: 'svg-icon-indesign',
  LTRM: 'svg-icon-lightroom-classic',
  LRCC: 'svg-icon-lightroom',
  PHSP: 'svg-icon-photoshop',
  PRLD: 'svg-icon-prelude',
  RUSH: 'svg-icon-premiere-rush',
  PPRO: 'svg-icon-premiere-pro',
  PRTM: 'svg-icon-primetime',
  SPRK: 'svg-icon-xd',
  MUSE: 'svg-icon-muse',
  FWKS: 'svg-icon-fireworks',
  ABD: 'svg-icon-app-builder',
  SBSTD: 'svg-icon-designer',
  SBSTP: 'svg-icon-painter',
  SBSTA: 'svg-icon-sampler',
});

export const formatBytes = (bytes, decimals = 2) => {
  if (!bytes) return null;

  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

export const validateEmail = (email) => {
  const regexp = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
  return regexp.test(email);
};

export const makeCCSupportedProductObjectsArray = (sapCodes, locs, selectedProduct) => {
  const uniqueProductSAPCodes = Array.from(new Set(sapCodes));
  const productNames = getProductNames(uniqueProductSAPCodes, locs);
  return uniqueProductSAPCodes
    .map((key, idx) => ({ key, name: productNames[idx] }))
    .sort((a, b) => {
      if (a.key === selectedProduct) {
        return -1;
      }
      if (b.key === selectedProduct) {
        return 1;
      }
      return azSort(a.name, b.name);
    });
};

export const getPartnerBadgeFunctionMaker = (SilverBadge, GoldBadge, PlatinumBadge) => (partnerLevel) => {
  if (!partnerLevel) {
    return null;
  }
  const partnerBadges = {
    silver: SilverBadge ? `${SilverBadge.match(/^\//) ? SilverBadge : `/${SilverBadge}`}` : null,
    gold: `${GoldBadge.match(/^\//) ? GoldBadge : `/${GoldBadge}`}`,
    platinum: `${PlatinumBadge.match(/^\//) ? PlatinumBadge : `/${PlatinumBadge}`}`,
  };
  const lowercased = partnerLevel?.toLowerCase();
  for (const partnerLevel of Object.keys(partnerBadges)) {
    if (lowercased?.includes(partnerLevel)) {
      return partnerBadges[partnerLevel];
    }
  }
};

export const formatCurrency = (amount) => {
  if (amount === 0) {
    return 0;
  } else {
    return '$' + Number(amount).toFixed(2);
  }
};

export const parseFromLocalStorage = (key) => {
  try {
    const unparsedValue = localStorage.getItem(key);
    return JSON.parse(unparsedValue);
  } catch (e) {
    console.error(`Error parsing ${key} from localStorage`, e);
    return null;
  }
};
